import type { UserType } from 'contexts/user.context';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';

import { ClientFilterItem, ProductFilterItem } from './ClientFilterItem';
import { buildClientsFilterTree, filterQuoteByClientFilter } from './Filter.helper';

export interface FilterByClientProps {
  userType: UserType;
  selectedClient?: string;
  selectedProduct?: string;
  quotes: QuoteIndexableData[];
  onByClientFilterChanged(clientId: string, product?: string): void;
  onTilesDeleted(quoteIds: string[]): void;
}

export const FilterByClient: FC<FilterByClientProps> = ({
  userType,
  selectedClient,
  selectedProduct,
  quotes,
  onByClientFilterChanged,
  onTilesDeleted,
}) => {
  const { formatMessage } = useIntl();

  const noClientLabel = useMemo(() => formatMessage({ id: 'dashboard.noClient' }), [formatMessage]);

  const clientFilters = useMemo(
    () => buildClientsFilterTree(quotes, noClientLabel, formatMessage),
    [formatMessage, noClientLabel, quotes],
  );

  const onClientTilesDeleted = useCallback(
    (clientId: string, product?: string) => {
      const quoteIdsToDelete = quotes.filter(q => filterQuoteByClientFilter(q, clientId, product)).map(q => q.quoteId);
      onTilesDeleted(quoteIdsToDelete);
    },
    [onTilesDeleted, quotes],
  );

  return (
    <ul className="nav nav-underline flex-column nav-hover">
      {userType === 'internalSale' &&
        clientFilters.map(({ id, name, count, products }) => (
          <ClientFilterItem
            key={id}
            id={id}
            name={name}
            count={count}
            selectedClientId={selectedClient}
            selectedProductId={selectedProduct}
            products={products}
            onSelected={onByClientFilterChanged}
            onTilesDeleted={onClientTilesDeleted}
          />
        ))}
      {userType === 'externalClient' &&
        clientFilters.length > 0 &&
        clientFilters[0].products.map(productFilter => (
          <ProductFilterItem
            key={productFilter.id}
            id={productFilter.id}
            name={productFilter.name}
            count={productFilter.count}
            selectedClientId={clientFilters[0].id}
            selectedProductId={selectedProduct}
            onSelected={onByClientFilterChanged}
            onTilesDeleted={onClientTilesDeleted}
          />
        ))}
    </ul>
  );
};

FilterByClient.displayName = 'FilterByClient';
